// Warm checkout from the plan pages, so the payment form is close to ready
// when the customer lands on it instead of starting from cold on the click.
//
// The grey block under "2 PAYMENT" is Stripe's own loader, and nearly all of
// its wait is Stripe's: stripe.js → its controller iframe → ~1.5–1.9 MB of
// iframe bundles, plus the elements/sessions call. Before this, none of that
// started until the customer reached checkout. Warming it on the plan page
// measured click → payment methods 1.03–1.05 s against 3.09–3.29 s cold
// (2026-10-03, owner's network, n=3+3).
//
// Once per page load, warming:
//   1. fetches the Checkout chunk through the same import() React.lazy uses;
//   2. calls getStripe(), creating THE shared instance checkout will mount on;
//   3. mounts a Payment Element offscreen so Stripe pulls its iframe bundles
//      into the cache, and destroys it once it reports ready (or times out).
//      The Stripe instance stays alive.
//
// It isn't free for a visitor who never buys: ~3.2–3.6 MB downloaded (Stripe
// ~2.4–2.8 MB plus ~0.75 MB of the hCaptcha Stripe starts; measured
// 2026-10-03), and main-thread work wherever Stripe's iframes share the
// page's process (Android without full site isolation, for one). So the idle
// path waits until the page's own content has had time to load and the
// browser is idle, and skips low-memory devices (useCheckoutWarmup); intent
// (a plan's buy control is hovered, pressed or focused) starts it at once.
// Save-Data and 2G connections skip it entirely. It sends no analytics,
// creates no PaymentIntent or order (deferred mode has no client secret), and
// never touches layout, focus or scroll.

import { useEffect } from 'react'

// The import() behind App.tsx's lazy(Checkout). Defined once, here, so the
// prefetch below and React.lazy resolve the very same chunk.
export const importCheckout = () => import('../routes/Checkout')

// 'failed': stripe.js (or our chunk for it) didn't load, so 'started' only
// ever means the customer clicked before an unfinished warm-up was done.
export type WarmupState = 'none' | 'started' | 'ready' | 'timeout' | 'failed'

let warmup: WarmupState = 'none'

/** How far this page load's warm-up got. Reported with checkout's timing. */
export function warmupState(): WarmupState {
  return warmup
}

// Set once checkout has mounted. From then on its own Payment Element loads
// what the warm-up would, so a warm-up still waiting for stripe.js mounts no
// hidden element beside it: that was a second elements/sessions call and
// iframe render at checkout's most latency-sensitive moment.
let checkoutOpened = false

/** Checkout calls this on mount. */
export function noteCheckoutOpened(): void {
  checkoutOpened = true
}

/** navigator.connection — Chromium only, undefined in Safari and Firefox. */
export function connectionInfo(): { saveData?: boolean; effectiveType?: string } | undefined {
  return (navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string }
  }).connection
}

// Generous: a slow connection may still be fetching. Past it the offscreen
// element goes anyway; whatever it downloaded is already in the cache.
const WARMUP_TIMEOUT_MS = 20_000

/** Start warming now. No-op after the first call in a page load, once
 *  checkout has opened (its own form did the warming), and on Save-Data or
 *  2G. `amountCents` only shapes the hidden element (Stripe needs ≥ 50);
 *  checkout mounts its own with the real amount. */
export function warmCheckout(amountCents: number): void {
  if (warmup !== 'none' || checkoutOpened) return
  const conn = connectionInfo()
  if (conn?.saveData || conn?.effectiveType === 'slow-2g' || conn?.effectiveType === '2g') return
  warmup = 'started'

  importCheckout().catch(() => {
    /* best-effort: the route loads it again on navigation */
  })

  // lib/stripe.ts is imported here on demand, not statically: a static import
  // puts it in the entry bundle, and the bundler then splits everything it
  // shares with Checkout out into an extra chunk that every page has to
  // fetch. Same module instance (and getStripe() singleton) either way.
  import('./stripe')
    .then(async ({ getStripe, STRIPE_APPEARANCE }) => {
      const stripe = await getStripe()
      if (!stripe) {
        warmup = 'failed'
        return
      }
      // The customer reached checkout before stripe.js arrived: its own
      // Payment Element is loading the same bundles now.
      if (checkoutOpened) return
      // Offscreen, not display:none, so Stripe lays it out and loads it as it
      // would a visible form. Fixed and pushed off to the left, so it takes
      // no space and adds no scrollbar; aria-hidden + inert keep it out of
      // screen readers and the tab order.
      const host = document.createElement('div')
      host.setAttribute('aria-hidden', 'true')
      host.inert = true
      host.style.cssText = 'position:fixed;left:-10000px;top:0;width:480px;pointer-events:none;'
      document.body.appendChild(host)
      const element = stripe
        .elements({
          mode: 'payment',
          amount: Math.max(50, amountCents),
          currency: 'usd',
          appearance: STRIPE_APPEARANCE,
        })
        .create('payment')
      let done = false
      const timer = setTimeout(() => finish('timeout'), WARMUP_TIMEOUT_MS)
      element.on('ready', () => finish('ready'))
      element.mount(host)

      function finish(outcome: 'ready' | 'timeout') {
        if (done) return
        done = true
        if (warmup === 'started') warmup = outcome
        clearTimeout(timer)
        element.destroy()
        host.remove()
      }
    })
    .catch(() => {
      // stripe.js, or our chunk for it, didn't load (blocked, offline).
      // Checkout calls getStripe() again, which retries.
      if (warmup === 'started') warmup = 'failed'
    })
}

// How long the page's own content gets, once the plan list has rendered,
// before the idle warm-up may start. In this SPA window 'load' fires before
// React has even requested the hero photo, and the plans arrive while it is
// still downloading, so starting right then competed with it: LCP +0.4 s on
// /destinations/jp at 1.6 Mbps (measured 2026-10-03). Intent doesn't wait.
const IDLE_GRACE_MS = 3000

/** Devices where the idle warm-up costs too much main-thread time. With
 *  Stripe's iframes in the page's own process (Android without full site
 *  isolation) and a 4x-slowed CPU, it measured ~2.3 s of long-task blocking
 *  on the plan page, spread over the seconds visitors start tapping, about
 *  half of it the hCaptcha Stripe starts (2026-10-03). Those devices warm on
 *  intent only. navigator.deviceMemory is Chromium's coarse RAM figure in GB
 *  (a power of two); 4 or less is roughly 6 GB and under. Safari and Firefox
 *  don't report it. (Not hardwareConcurrency: WebKit reports 4 for every
 *  device with fewer than 8 cores, iPhones included.) */
function lowEndDevice(): boolean {
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory
  return memory !== undefined && memory <= 4
}

/** Plan pages: warm checkout once the page has loaded, its content has had
 *  IDLE_GRACE_MS, and the browser is idle; never at idle on a low-end device.
 *  `amountCents` is the cheapest visible plan; undefined (plans still
 *  loading) waits. */
export function useCheckoutWarmup(amountCents: number | undefined): void {
  useEffect(() => {
    if (amountCents === undefined || warmup !== 'none' || lowEndDevice()) return
    let idleId: number | undefined
    let timer: ReturnType<typeof setTimeout> | undefined
    const run = () => warmCheckout(amountCents)
    const whenIdle = () => {
      // Safari has no requestIdleCallback; the grace period stands in.
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(run, { timeout: 3000 })
      } else {
        run()
      }
    }
    const afterLoad = () => {
      timer = setTimeout(whenIdle, IDLE_GRACE_MS)
    }
    if (document.readyState === 'complete') afterLoad()
    else window.addEventListener('load', afterLoad, { once: true })
    return () => {
      window.removeEventListener('load', afterLoad)
      if (idleId !== undefined) window.cancelIdleCallback(idleId)
      clearTimeout(timer)
    }
  }, [amountCents])
}

/** Spread onto a plan's buy control: hovering, pressing or focusing it is
 *  intent enough to start warming right away rather than at idle. */
export function warmOnIntent(amountCents: number) {
  const start = () => warmCheckout(amountCents)
  return { onPointerEnter: start, onPointerDown: start, onFocus: start }
}

// The last plan click, for checkout's click → ready timing. performance.now()
// is per document, which SPA navigation keeps; a reload or a direct link
// starts with no click, which is exactly right.
let planClick: { planId: string; at: number } | null = null

/** Call from a plan's click handler, right before navigating to checkout. */
export function notePlanClick(planId: string): void {
  planClick = { planId, at: performance.now() }
}

/** The click that led to this checkout visit, if there was one: the same
 *  plan, moments ago. Read once at checkout mount, then forgotten. */
export function planClickFor(planId: string): number | null {
  const c = planClick
  return c && c.planId === planId && performance.now() - c.at < 30_000 ? c.at : null
}

export function forgetPlanClick(): void {
  planClick = null
}
